import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useTranslation();
  const handleLogout = () => { logout(); navigate('/login'); };

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  useEffect(() => { closeSidebar(); }, [location.pathname, closeSidebar]);

  const navItems = [
    { to: '/', icon: '⬡', label: t('nav.dashboard'), end: true },
    { to: '/campo', icon: '🌿', label: t('nav.campo') },
    { to: '/lote', icon: '⚡', label: t('nav.lote') },
    { to: '/zonas', icon: '📍', label: t('nav.zonas') },
    { to: '/colmenas', icon: '🐝', label: t('nav.colmenas') },
    { to: '/tareas', icon: '📋', label: t('nav.tareas') },
    { to: '/bitacora', icon: '📓', label: t('nav.bitacora') },
    { to: '/temporada', icon: '🍯', label: t('nav.temporada') },
    { to: '/clima', icon: '🌤️', label: t('nav.clima') },
    { to: '/ajustes', icon: '⚙️', label: t('nav.ajustes') },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {isMobile && (
        <div className="mobile-topbar">
          <button onClick={() => setSidebarOpen(true)} className="hamburger-btn">☰</button>
          <span className="mobile-brand">🍯 Colmena</span>
        </div>
      )}

      {isMobile && sidebarOpen && (
        <div className="sidebar-backdrop" onClick={closeSidebar} />
      )}

      <aside className={`sidebar ${isMobile ? (sidebarOpen ? 'sidebar-mobile-open' : 'sidebar-mobile-closed') : ''}`}>
        <div style={S.brand}>
          <span style={{ fontSize: 24 }}>🍯</span>
          <span style={S.brandText}>Colmena</span>
          {isMobile && (
            <button onClick={closeSidebar} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#F5C47A', fontSize: 22, cursor: 'pointer' }}>✕</button>
          )}
        </div>
        <nav style={S.nav}>
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              style={({ isActive }) => ({ ...S.navItem, ...(isActive ? S.navActive : {}) })}>
              <span style={{ fontSize: 15, width: 20, textAlign: 'center' }}>{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div style={S.userBox}>
          <div style={S.avatar}>{user?.name?.[0]?.toUpperCase() || '?'}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.userName}>{user?.name}</div>
            <div style={S.userEmail}>{user?.email}</div>
          </div>
          <button onClick={handleLogout} style={S.logoutBtn} title={t('nav.logout')}>↩</button>
        </div>
      </aside>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}

const S = {
  brand: { display: 'flex', alignItems: 'center', gap: 10, padding: '24px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  brandText: { fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: '#F5C47A', fontSize: 20 },
  nav: { flex: 1, padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' },
  navItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, color: 'rgba(245,196,122,0.65)', fontSize: 14, textDecoration: 'none', transition: 'all 0.15s' },
  navActive: { background: 'rgba(232,146,42,0.2)', color: '#F5C47A', fontWeight: 500, boxShadow: 'inset 2px 0 0 #E8922A' },
  userBox: { padding: 16, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 10 },
  avatar: { width: 34, height: 34, borderRadius: '50%', background: 'rgba(232,146,42,0.3)', color: '#F5C47A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, flexShrink: 0 },
  userName: { color: '#F5C47A', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userEmail: { color: 'rgba(245,196,122,0.5)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  logoutBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(245,196,122,0.5)', fontSize: 16, padding: 4 },
};
